import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtMoney } from "@/components/status";

export default function SapSendDialog({ intoStoreDate, open, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    adminApi.sapPreview(intoStoreDate).then(setPreview).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [open, intoStoreDate]);

  const send = async () => {
    setSending(true);
    try {
      const r = await adminApi.sapSend(intoStoreDate, !!preview?.already_sent);
      toast.success(`Sent to SAP — sales order ${r.sap_sales_order}`);
      onSent(r); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const blocked = !preview || !preview.configured || preview.missing_material_codes.length > 0 || preview.items.length === 0 || !preview.into_dc_date;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sap-send-modal">
        <DialogHeader><DialogTitle className="font-serif">Send to SAP HANA</DialogTitle></DialogHeader>
        {!preview ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-3 text-sm text-[#28282E]">
            <p>One consolidated sales order under Metcash account <strong data-testid="sap-sold-to">{preview.sold_to}</strong>, dated by the <strong>DC date</strong>.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-[#FBEEF4] p-3 text-xs">
              <div>DC date (SAP delivery)</div><div className="font-bold" data-testid="sap-dc-date">{preview.into_dc_date || "—"}</div>
              <div>Into store</div><div className="font-bold">{preview.into_store_date}</div>
              <div>Customer PO reference</div><div className="font-bold">{preview.po_number}</div>
              <div>Source orders</div><div className="font-bold">{preview.order_count}</div>
              <div>Lines / cartons</div><div className="font-bold" data-testid="sap-lines">{preview.items.length} lines · {Math.round(preview.total_cartons)} cartons</div>
              <div>PO total</div><div className="font-bold">{fmtMoney(preview.grand_total)}</div>
            </div>
            <ul className="max-h-36 overflow-y-auto rounded border border-slate-200 text-xs">
              {preview.items.map((it) => <li key={it.flower_id} className="flex justify-between border-b border-slate-100 px-2 py-1 last:border-0"><span>{it.name} <span className="font-mono text-[#28282E]/50">{it.material}</span></span><span className="font-bold">{it.quantity} CAR</span></li>)}
              {preview.items.length === 0 && <li className="px-2 py-3 text-center text-[#28282E]/55">No ordered cartons for this date.</li>}
            </ul>
            {preview.already_sent && <div data-testid="sap-already-sent" className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>Already sent as SAP order <strong>{preview.already_sent.sap_sales_order}</strong> on {String(preview.already_sent.sent_at).slice(0, 16).replace("T", " ")}. Sending again creates a <strong>second</strong> SAP order.</span></div>}
            {preview.missing_material_codes.length > 0 && <div data-testid="sap-missing-codes" className="flex items-start gap-2 rounded-lg bg-red-50 p-2 text-xs text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>Missing SAP material codes: {preview.missing_material_codes.join(", ")}. Add them in Flower Images → Details.</span></div>}
            {!preview.configured && <div data-testid="sap-not-configured" className="flex items-start gap-2 rounded-lg bg-red-50 p-2 text-xs text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>SAP is not configured. Add secrets: {preview.missing_settings.join(", ")}.</span></div>}
            {!blocked && <p className="flex items-center gap-2 text-xs text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Ready to send. The report will be locked for store edits afterwards.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button data-testid="sap-send-confirm" onClick={send} disabled={blocked || sending} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} {preview?.already_sent ? "Re-send to SAP" : "Send to SAP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
